"use client"

import {UiErrorText} from "@/components/i18n/ui-error-text"
import { useState, type ReactNode } from "react"
import { ChevronDown, Loader2, Table2 } from "lucide-react"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Button } from "@/components/ui/button"
import { useAiDisclosureSummaryQuery, useAiDisclosureHistoryQuery, useAiDisclosureDetailsQuery } from "@/hooks/queries/ai-disclosure-report"
import { DISCLOSURE_REPORT_PAGE_SIZE } from "@/lib/ai-disclosure/report-pagination"
import { useTranslations } from "next-intl"



const TABLE_CLASS = "w-full text-left text-sm [&_th]:px-4 [&_th]:py-2 [&_th]:font-medium [&_td]:px-4 [&_td]:py-3 [&_td]:align-top [&_tbody_tr]:border-t"
type RangeProps = { from: string; to: string }

export function DisclosureReportTables({ from, to }: RangeProps) {

  const tUi = useTranslations("Organization.DisclosureReportTables")

  return (
    <div className="min-w-0 space-y-3">
      <ReportSection title={tUi("byChannelAndFirstRecordedOutcome")} description={tUi("channelOutcomesAndRecordedCoverageForTheSelected")}>
        <SummaryReport from={from} to={to} />
      </ReportSection>
      <ReportSection title={tUi("aIDisclosureSettingHistory")} description={tUi("theLastRecordedStateBeforeThisRangeAnd")}>
        <HistoryReport from={from} to={to} />
      </ReportSection>
      <ReportSection title={tUi("conversationDetails")} description={tUi("value0RecordsPerPageExpandEvidenceToReview", { value0: String(DISCLOSURE_REPORT_PAGE_SIZE) })}>
        <DetailsReport from={from} to={to} />
      </ReportSection>
    </div>
  )
}

function SummaryReport({ from, to }: RangeProps) {
  const tUi = useTranslations("Organization.DisclosureReportTables")

  const report = useAiDisclosureSummaryQuery(from, to)
  if (!report.data) return <SectionQueryState error={report.error} retry={() => void report.refetch()} />
  const rows = report.data.filter(row => row.noticeKind === "ai")
  const countStatus = (status: string) => rows.filter(row => row.status === status).reduce((count, row) => count + row.conversations, 0)
  const total = rows.reduce((count, row) => count + row.conversations, 0)
  const delivered = countStatus("delivered")
  const disabled = countStatus("disabled")
  const denominator = total - disabled
  const coverage = denominator > 0 ? `${((delivered / denominator) * 100).toFixed(1)}%` : "—"
  const metrics = [[tUi("aiConversations"), String(total)], [tUi("delivered"), String(delivered)], [tUi("failed"), String(countStatus("failed"))],
    [tUi("missingRecords"), String(countStatus("missing"))], [tUi("generatedOnly"), String(countStatus("generated"))],
    [tUi("disabled"), String(disabled)], [tUi("recordedCoverage"), coverage]]
  return <><div className="w-full overflow-x-auto"><table className={TABLE_CLASS} aria-label={tUi("byChannelAndFirstRecordedOutcome")}>
          <thead className="bg-muted"><tr>{[tUi("channel"), tUi("notice"), tUi("status"), tUi("conversations")].map(label => <th key={label} scope="col">{label}</th>)}</tr></thead>
          <tbody>{report.data.map(row => (
            <tr key={`${row.channel}-${row.noticeKind}-${row.status}`}>
              <td><span key={row.channel}>{channelLabel(row.channel, tUi)}</span></td><td><span key={row.noticeKind}>{noticeLabel(row.noticeKind, tUi)}</span></td><td><span key={row.status}>{outcomeLabel(row.status, tUi)}</span></td><td><span key={row.conversations}>{row.conversations}</span></td>
            </tr>
          ))}
          {report.data.length === 0 && <tr><td colSpan={4}><span key={tUi("noRecordedAIInteractionsInThisPeriod")}>{tUi("noRecordedAIInteractionsInThisPeriod")}</span></td></tr>}
          </tbody>
        </table></div>
        <table className={`${TABLE_CLASS} table-fixed break-words border-t`} aria-label={tUi("recordedCoverage")}>
          <thead className="bg-muted"><tr><th scope="col"><span key={tUi("metric")}>{tUi("metric")}</span></th><th scope="col"><span key={tUi("value")}>{tUi("value")}</span></th></tr></thead>
          <tbody>{metrics.map(([label, value]) => <tr key={label}><th scope="row">{label}</th><td><span key={value}>{value}</span></td></tr>)}</tbody>
        </table>
</>
}

function HistoryReport({ from, to }: RangeProps) {
  const tUi = useTranslations("Organization.DisclosureReportTables")

  const report = useAiDisclosureHistoryQuery(from, to)
  if (!report.data) return <SectionQueryState error={report.error} retry={() => void report.refetch()} />
  return <>        <table className={`${TABLE_CLASS} min-w-[640px]`} aria-label={tUi("aIDisclosureSettingHistory")}>
          <thead className="bg-muted"><tr>{[tUi("effectiveUTC"), tUi("change"), tUi("aiAnnouncements"), tUi("recordedUTC"), tUi("changedBy"), tUi("confirmation")].map(label => <th key={label} scope="col">{label}</th>)}</tr></thead>
          <tbody>{report.data.map(change => (
            <tr key={change.id}>
              <td className="whitespace-nowrap">{change.effectiveAt}</td><td key={change.source === "initial_state" ? tUi("initialState") : tUi("settingChanged")}>{change.source === "initial_state" ? tUi("initialState") : tUi("settingChanged")}</td>
              <td key={change.enabled ? tUi("enabled") : tUi("disabled")}>{change.enabled ? tUi("enabled") : tUi("disabled")}</td><td>{change.recordedAt}</td>
              <td className="max-w-48 break-words">{change.actorUserId ?? tUi("noUserActionRecorded")}</td>
              <td className="min-w-48 max-w-80 whitespace-pre-wrap break-words">{change.acceptedConfirmationText ?? "—"}</td>
            </tr>
          ))}
          {report.data.length === 0 && <tr><td colSpan={6}><span key={tUi("noSettingHistoryRecordedForThisPeriod")}>{tUi("noSettingHistoryRecordedForThisPeriod")}</span></td></tr>}
          </tbody>
        </table>
</>
}

function DetailsReport({ from, to }: RangeProps) {

  const tUi = useTranslations("Organization.DisclosureReportTables")

  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined])
  const report = useAiDisclosureDetailsQuery(from, to, cursors.at(-1))
  if (!report.data) return <SectionQueryState error={report.error} retry={() => void report.refetch()} />
  return <>        <table className={`${TABLE_CLASS} min-w-[680px]`} aria-label={tUi("conversationDetails")}>
          <thead className="bg-muted"><tr>{[tUi("channelNotice"), tUi("conversationId"), tUi("firstActivityUTC"), tUi("firstLatestOutcome"), tUi("aiSetting"), tUi("evidence")].map(label => <th key={label} scope="col">{label}</th>)}</tr></thead>
          <tbody>{report.data.details.map(row => (
            <tr key={row.key}>
              <td><span key={`${row.channel}-${row.noticeKind}`}>{tUi("value0Value1", { value0: channelLabel(row.channel, tUi), value1: noticeLabel(row.noticeKind, tUi) })}</span></td><td className="max-w-52 break-all">{row.conversationRef}</td>
              <td>{row.firstActivityAt}</td><td><span key={`${row.status}-${row.latestStatus}`}>{tUi("value0Value1", { value0: outcomeLabel(row.status, tUi), value1: outcomeLabel(row.latestStatus, tUi) })}</span></td><td><span key={String(row.enabledAtActivity)}>{settingDescription(row.enabledAtActivity, tUi)}</span></td>
              <td className="min-w-64">
                {row.events.length === 0 ? <span><span key={tUi("noRecordedEvidence")}>{tUi("noRecordedEvidence")}</span></span> : (
                  <details><summary className="cursor-pointer font-medium"><span key={tUi("viewEvidence")}>{tUi("viewEvidence")}</span></summary>
                    <table className={`${TABLE_CLASS} mt-2`} aria-label={tUi("evidenceForValue0Value1", { value0: String(row.conversationRef), value1: String(row.noticeKind) })}>
                      <thead className="bg-muted"><tr>{[tUi("timeUTC"), tUi("channel"), tUi("status"), tUi("language"), tUi("disclosureText"), tUi("failureReason")].map(label => <th key={label} scope="col">{label}</th>)}</tr></thead>
                      <tbody>{row.events.map((event, index) => (
                        <tr key={`${event.occurredAt}-${index}`}><td>{event.occurredAt}</td><td>{channelLabel(event.channel, tUi)}</td><td>{outcomeLabel(event.status, tUi)}</td><td>{event.language ?? "—"}</td>
                          <td className="min-w-48 whitespace-pre-wrap break-words">{event.noticeText ?? "—"}</td><td className="min-w-48 break-words">{event.failureReason ?? "—"}</td></tr>
                      ))}</tbody>
                    </table>
                  </details>
                )}
              </td>
            </tr>
          ))}
          {report.data.details.length === 0 && <tr><td colSpan={6}><span key={tUi("noConversationDetailsInThisPeriod")}>{tUi("noConversationDetailsInThisPeriod")}</span></td></tr>}
          </tbody>
        </table>

    <div className="flex flex-wrap items-center gap-3 border-t px-4 py-3">
      <Button variant="outline" disabled={cursors.length === 1 || report.isFetching}
        onClick={() => setCursors(previous => previous.slice(0, -1))}><span key={tUi("previous")}>{tUi("previous")}</span></Button>
      <span key={cursors.length} className="text-sm">{<span key={tUi("pageValue0", { value0: String(cursors.length) })}>{tUi("pageValue0", { value0: String(cursors.length) })}</span>}</span>
      <Button variant="outline" disabled={!report.data.nextCursor || report.isFetching}
        onClick={() => setCursors(previous => [...previous, report.data!.nextCursor!])}><span key={tUi("next")}>{tUi("next")}</span></Button>
    </div>
  </>
}

function SectionQueryState({ error, retry }: { error: Error | null; retry: () => void }) {
  const tCommonUi = useTranslations("Common")
  const tUi = useTranslations("Organization.DisclosureReportTables")

  if (error) return <div className="space-y-2 p-4"><p role="alert" className="text-sm text-destructive"><UiErrorText message={error.message} /></p><Button variant="outline" onClick={retry}><span key={tCommonUi("retry")}>{tCommonUi("retry")}</span></Button></div>
  return <div role="status" className="flex items-center gap-2 p-4 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /><span><span key={tUi("loadingSection")}>{tUi("loadingSection")}</span></span></div>
}

function ReportSection({ title, description, children }: { title: string; description: string; children: ReactNode }) {
  const [expanded, setExpanded] = useState(false)
  return (
    <Collapsible open={expanded} onOpenChange={setExpanded} className="min-w-0 rounded-lg border border-primary/20">
      <CollapsibleTrigger asChild>
        <button type="button" aria-label={title} className="group flex w-full items-center gap-3 rounded-lg bg-muted/50 px-4 py-3 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"><Table2 className="h-4 w-4" /></span>
          <span className="min-w-0 flex-1"><span className="block text-sm font-semibold">{title}</span><span className="mt-0.5 block text-xs text-muted-foreground">{description}</span></span>
          <ChevronDown className="h-4 w-4 shrink-0 transition-transform group-data-[state=open]:rotate-180" />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="min-w-0"><div className="max-w-full overflow-x-auto rounded-b-lg border-t">{expanded ? children : null}</div></CollapsibleContent>
    </Collapsible>
  )
}

function settingDescription(enabled: boolean | null, tUi: ReturnType<typeof useTranslations<"Organization.DisclosureReportTables">>): string {
  if (enabled === null) return tUi("unknown")
  return enabled ? tUi("enabled") : tUi("disabled")
}

type ReportTranslator = ReturnType<typeof useTranslations<"Organization.DisclosureReportTables">>

function outcomeLabel(status: string, t: ReportTranslator): string {
  switch (status) {
    case "delivered": return t("delivered")
    case "failed": return t("failed")
    case "missing": return t("missingRecords")
    case "generated": return t("generatedOnly")
    case "disabled": return t("disabled")
    default: return status
  }
}

function channelLabel(channel: string, t: ReportTranslator): string {
  switch (channel) {
    case "voice": return t("voice")
    case "voice_inbound": return t("incomingCall")
    case "voice_outbound": return t("outgoingCall")
    case "web_chat": return t("webChat")
    case "email": return "email"
    case "whatsapp": return "WhatsApp"
    case "facebook": return "Facebook"
    case "instagram": return "Instagram"
    default: return channel
  }
}

function noticeLabel(notice: string, t: ReportTranslator): string {
  switch (notice) {
    case "ai": return t("aiNotice")
    case "recording": return t("recordingNotice")
    default: return notice
  }
}
